import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { canView, getUserType } from "../permissions";
import API_BASE from "../config";

const EditUser = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = location.state?.user;
    const userType = getUserType();
    const allowAccess = canView(userType, "settings");

    const [form, setForm] = useState({
        id: "", name: "", gmail: "", type: "", phone: "", project: "", city: "", address: "", client_id: "",
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!allowAccess) { navigate("/dashboard"); return; }
        if (!user) { navigate("/dashboard/settings"); return; }
        setForm({
            id: user.id || "", name: user.name || "", gmail: user.gmail || "",
            type: user.type || "", phone: user.phone || "", project: user.project || "",
            city: user.city || "", address: user.address || "", client_id: user.client_id || "",
        });
    }, [user, navigate, allowAccess]);

    if (!allowAccess) return null;

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true); setError(""); setSuccess("");

        const formData = new URLSearchParams();
        Object.keys(form).forEach((key) => {
            if (key !== "id") formData.append(key, form[key]);
        });

        fetch(`${API_BASE}/api/updatedatas`, {
            method: "POST",
            headers: { Authorization: "1", Table: "users", Id: form.id },
            body: formData,
        })
            .then((r) => r.json())
            .then((json) => {
                setSaving(false);
                if (json.status == true) { setSuccess("User updated!"); setTimeout(() => navigate("/dashboard/settings"), 1500); }
                else setError(json.message || "Failed to update.");
            })
            .catch(() => { setSaving(false); setError("Error updating user."); });
    };

    return (
        <>
            <style>{`
        .eu-page{max-width:700px;margin:0 auto}
        .eu-hdr{display:flex;align-items:center;gap:14px;margin-bottom:24px}
        .eu-back{padding:8px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;color:#334155;cursor:pointer;font-size:14px;font-weight:500;font-family:'Inter',sans-serif;transition:all .2s;display:flex;align-items:center;gap:6px}
        .eu-back:hover{background:#f1f5f9;border-color:#cbd5e1}
        .eu-title{font-size:22px;font-weight:700;color:#0f172a}
        .eu-sub{font-size:13px;color:#64748b}
        .eu-card{background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:28px;box-shadow:0 1px 3px rgba(0,0,0,.04)}
        .eu-sec{font-size:14px;font-weight:600;color:#0f172a;margin-bottom:16px;padding-bottom:10px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;gap:8px}
        .eu-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:28px}
        .eu-field{display:flex;flex-direction:column;gap:6px}
        .eu-field.full{grid-column:1/-1}
        .eu-field label{font-size:12px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.4px}
        .eu-field input,.eu-field select{padding:11px 14px;border-radius:10px;border:1px solid #e2e8f0;font-size:14px;font-family:'Inter',sans-serif;color:#1e293b;background:#f8fafc;outline:none;transition:all .2s}
        .eu-field input:focus,.eu-field select:focus{border-color:#93c5fd;box-shadow:0 0 0 3px rgba(59,130,246,.08);background:#fff}
        .eu-actions{display:flex;gap:12px;margin-top:8px;padding-top:20px;border-top:1px solid #f1f5f9}
        .eu-save{padding:12px 28px;background:linear-gradient(135deg,#2563eb,#3b82f6);color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;transition:all .2s;display:flex;align-items:center;gap:8px}
        .eu-save:hover:not(:disabled){background:linear-gradient(135deg,#1d4ed8,#2563eb);box-shadow:0 4px 14px rgba(37,99,235,.3);transform:translateY(-1px)}
        .eu-save:disabled{opacity:.6;cursor:not-allowed;transform:none}
        .eu-cancel{padding:12px 24px;background:#f8fafc;color:#64748b;border:1px solid #e2e8f0;border-radius:10px;font-size:14px;font-weight:500;cursor:pointer;font-family:'Inter',sans-serif;transition:all .2s}
        .eu-cancel:hover{background:#f1f5f9;color:#334155}
        .eu-spinner{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:eu-sp .6s linear infinite}
        @keyframes eu-sp{to{transform:rotate(360deg)}}
        .eu-alert{padding:12px 16px;border-radius:10px;font-size:13px;margin-bottom:20px;font-weight:500}
        .eu-alert-ok{background:#ecfdf5;color:#059669;border:1px solid #a7f3d0}
        .eu-alert-err{background:#fef2f2;color:#dc2626;border:1px solid #fecaca}
        @media(max-width:768px){.eu-grid{grid-template-columns:1fr}.eu-card{padding:20px}.eu-actions{flex-direction:column}}
      `}</style>

            <div className="eu-page">
                <div className="eu-hdr">
                    <button className="eu-back" onClick={() => navigate("/dashboard/settings")}>← Back</button>
                    <div>
                        <div className="eu-title">Edit User</div>
                        <div className="eu-sub">{form.name} — {form.type}</div>
                    </div>
                </div>

                {success && <div className="eu-alert eu-alert-ok">✅ {success}</div>}
                {error && <div className="eu-alert eu-alert-err">⚠️ {error}</div>}

                <form className="eu-card" onSubmit={handleSubmit}>
                    <div className="eu-sec">👤 User Details</div>
                    <div className="eu-grid">
                        <div className="eu-field"><label>Full Name</label><input name="name" value={form.name} onChange={handleChange} required /></div>
                        <div className="eu-field"><label>Email</label><input name="gmail" type="email" value={form.gmail} onChange={handleChange} /></div>
                        <div className="eu-field">
                            <label>Role</label>
                            <select name="type" value={form.type} onChange={handleChange} required>
                                <option value="">Select Role</option>
                                <option value="admin">Admin</option>
                                <option value="Accountant">Accountant</option>
                                <option value="Sales Executive">Sales Executive</option>
                                <option value="After Sales">After Sales</option>
                                <option value="Project Engineer">Project Engineer</option>
                            </select>
                        </div>
                        <div className="eu-field"><label>Phone</label><input name="phone" value={form.phone} onChange={handleChange} /></div>
                        <div className="eu-field"><label>Client ID</label><input name="client_id" value={form.client_id} onChange={handleChange} /></div>
                        <div className="eu-field"><label>Project</label><input name="project" value={form.project} onChange={handleChange} placeholder="e.g. SEI, SHC" /></div>
                        <div className="eu-field"><label>City</label><input name="city" value={form.city} onChange={handleChange} /></div>
                        <div className="eu-field full"><label>Address</label><input name="address" value={form.address} onChange={handleChange} /></div>
                    </div>

                    <div className="eu-actions">
                        <button type="submit" className="eu-save" disabled={saving}>
                            {saving && <span className="eu-spinner" />}
                            {saving ? "Saving..." : "💾 Save Changes"}
                        </button>
                        <button type="button" className="eu-cancel" onClick={() => navigate("/dashboard/settings")}>Cancel</button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditUser;
